import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import Loading from '../../../../components/Loading';
import { Heading, Subheading } from '../../../../components/Heading';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';
import { useAppAnnouncements } from '../../../../hooks/useAppAnnouncement';
import { UserContext } from '../../../../context/userContext';
import { Textarea } from '../../../../components/Textarea';
import MultipleImageUpload from '../../../../components/MultipleImageUpload';

interface AddAnnouncement {
    title: string;
    content: string;
    created_user_id: bigint;
}

interface FileWithPreview {
    file: File;
    id: string;
    preview?: string;
    type: 'image' | 'pdf';
}

const AddAppAnnouncementPage = () => {
    const navigate = useNavigate();

    const [announcement, setAnnouncement] = useState<AddAnnouncement>({
        title: '',
        content: '',
        created_user_id: BigInt(0),
    });
    const { addAnnouncementWithMultipleImages, loading, error } = useAppAnnouncements();
    const { currentUser } = useContext(UserContext);
    const [fileItems, setFileItems] = useState<FileWithPreview[]>([]);

    // created_user_id will be set from current logged in user below

    useEffect(() => {
        if (currentUser) {
            setAnnouncement(prev => ({ ...prev, created_user_id: currentUser.id }));
        }
    }, [currentUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setAnnouncement(prev => ({
            ...prev,
            [name]: (name === "created_user_id") ? BigInt(value) : value,
        }));
    };

    const validateForm = () => {
        if (!announcement.title) {
            showErrorToast('Title is required');
            return false;
        }
        if (!announcement.content) {
            showErrorToast('Content is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const announcementToSend = {
                ...announcement,
                created_user_id: announcement?.created_user_id.toString(), // ✅ Ensure it's a string
            };

            let res;
            if (fileItems.length > 0) {
                const files = fileItems.map(item => item.file);
                res = await addAnnouncementWithMultipleImages(announcementToSend, files);
            } else {
                // If no files, you might want to use the regular addAnnouncement method
                // For now, we'll use the multiple images method with empty array
                res = await addAnnouncementWithMultipleImages(announcementToSend, []);
            }

            console.log('Add Announcement Response:', res);
            if (res?.dbsuccess) {
                showLoadingToast(`Creating Announcement ...`, 100);
                setTimeout(() => {
                    navigate('/gp/app/announcements');
                    showSuccessToast(`Announcement added successfully`);
                }, 1000);
            } else {
                showErrorToast(`Error creating announcement: ${res?.message || 'Unknown error'}`);
            }
        } catch (err) {
            showErrorToast(`Failed to add announcement: ${err}`);
        }
    };

    if (loading) return <Loading />;
    if (error) return <p>{error}</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Add App Announcement</Heading>
                <div className='mt-5 flex lg:ml-4 lg:mt-0'>
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className='ml-3' disabled={!announcement.title || !announcement.content}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Announcement Details</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            A brief summary highlighting key personal details.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Title</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        type="text"
                                        aria-label="Title"
                                        name="title"
                                        value={announcement.title?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Content</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Textarea
                                        aria-label="Content"
                                        name="content"
                                        value={announcement.content?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <MultipleImageUpload
                                    files={fileItems}
                                    onFilesChange={setFileItems}
                                    maxFiles={5}
                                    className="mt-2"
                                />
                            </section>
                        </div>

                    </div>
                </div>
            </Card>

            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={!announcement.title || !announcement.content}>Save</Button>
            </div>
        </form>
    );
};

export default AddAppAnnouncementPage;
